import { supabase } from '../../lib/supabase';
import { ActivityLog } from '../../types';

export const LogService = {
  async createLog(
    action: ActivityLog['action'],
    entityType: ActivityLog['entity_type'],
    entityId?: string,
    details?: Record<string, unknown>,
    currentUser?: { id: string; email?: string },
  ) {
    try {
      let userId = currentUser?.id;
      let userEmail = currentUser?.email;

      if (!userId) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          userId = user.id;
          userEmail = user.email;
        }
      }

      if (!userId) return;

      // 1. Try using RPC (Secure & Robust for Auth events)
      // Use authenticated client (supabase) so this runs with the current user's context.
      const { error: rpcError } = await supabase.rpc('log_activity', {
        p_action: action,
        p_entity_type: entityType,
        p_entity_id: entityId || null,
        p_details: details || null,
        p_user_id: userId,
        p_user_email: userEmail,
      });

      // 2. Fallback to direct insert if RPC fails (e.g. function not exists)
      if (rpcError) {
        // Only log error if it's NOT "function not found" to avoid noise
        if (rpcError.code !== 'PGRST202') {
          console.warn('RPC log_activity failed, falling back to direct insert:', rpcError.message);
        }

        const { error } = await supabase.from('activity_logs').insert({
          user_id: userId,
          user_email: userEmail,
          action,
          entity_type: entityType,
          entity_id: entityId,
          details,
        });

        if (error) {
          console.error('Error creating activity log (Insert):', error);
        }
      }
    } catch (err) {
      console.error('Error in LogService:', err);
    }
  },

  async getLogs(limit = 50) {
    const { data: logs, error } = await supabase
      .from('activity_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Fetch user profiles to get full names
    if (logs && logs.length > 0) {
      const userIds = [...new Set(logs.map((log) => log.user_id).filter(Boolean))];

      if (userIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);

        if (profileError) {
          console.error('Error fetching profiles for logs:', profileError);
        }

        if (profiles) {
          const profileMap = new Map(profiles.map((p) => [p.id, p.full_name]));

          return logs.map((log) => ({
            ...log,
            user_name: profileMap.get(log.user_id) || log.user_email?.split('@')[0] || 'Unknown',
          })) as ActivityLog[];
        }
      }
    }

    return logs as ActivityLog[];
  },
};
